import type { LucideIcon } from 'lucide-react';
import { TableCellsMerge, TableCellsSplit } from 'lucide-react';
import { equals } from 'ramda';
import type { Editor } from 'slate';
import { openSplitTableCellsPopover } from 'src/editor/popovers';
import { Queries } from 'src/editor/queries';
import { ClientTransforms } from 'src/editor/transforms';
import { useAuthorizedTransforms } from 'src/hooks';
import { useEditor, useEditorLoading, useSlateSelector } from 'src/layout-engine';
import { useEditingMode } from 'src/store/document/selectors';

/** An action on the table's structure, shared by the ribbon, the table tab and the context menu. */
export interface TableAction {
  /** Whether the selection allows the action; always false outside editing mode. */
  enabled: boolean;
  icon: LucideIcon;
  key: 'makeRectangular' | 'merge' | 'split';
  label: string;
  onClick: () => void;
  /** Whether the action is offered at all; a hidden action is left out rather than disabled. */
  visible: boolean;
}

/** The merge and split cell actions shared by the ribbon, the table tab and the context menu. */
export function useTableCellMergeSplitActions(): TableAction[] {
  const editor = useEditor();
  const { AuthorizedTransforms } = useAuthorizedTransforms();
  const editorLoading = useEditorLoading();
  const { editing, editingMode } = useEditingMode();
  const { canMerge, canSplit } = useSlateSelector(getMergeSplitAvailability, equals);
  const isEditable = editing && !editorLoading;

  return [
    {
      enabled: isEditable && canMerge,
      icon: TableCellsMerge,
      key: 'merge',
      label: 'Merge cells',
      onClick: mergeTableCells,
      visible: true,
    },
    {
      enabled: isEditable && canSplit,
      icon: TableCellsSplit,
      key: 'split',
      label: 'Split cells',
      onClick: openSplitTableCellsPopover,
      visible: true,
    },
  ];

  function mergeTableCells(): void {
    if (!editingMode) return;

    AuthorizedTransforms.mergeTableCells(editor, { editingMode });
    ClientTransforms.focus(editor);
  }
}

function getMergeSplitAvailability(editor: Editor): { canMerge: boolean; canSplit: boolean } {
  const hasRegion = Boolean(Queries.getSelectedGridRegion(editor));

  return { canMerge: hasRegion && Queries.getIsGridSelection(editor), canSplit: hasRegion };
}
